/** Score: the furthest distance from the cabin, in metres, and its display form. */
import { isqrt } from "./hash.ts";

/** One tile is 50 metres. */
export const METRES_PER_TILE = 50;

/**
 * The languages the core writes distances and the share text in, by their BCP 47 codes, in the
 * order of the table in spec/core-spec.md, section 8 (decision 0026).
 */
export const LOCALES = ["en", "sv", "de", "es", "fr", "ja", "ko", "zh-Hans", "zh-Hant"] as const;

export type Locale = (typeof LOCALES)[number];

/** Each language's decimal separator, as section 8's table gives it. */
const DECIMAL: Readonly<Record<Locale, string>> = {
  en: ".",
  sv: ",",
  de: ",",
  es: ",",
  fr: ",",
  ja: ".",
  ko: ".",
  "zh-Hans": ".",
  "zh-Hant": ".",
};

/** Metres from the squared tile distance: floor(sqrt(d2 * 2500)). */
export function metresFromDistanceSq(d2: number): number {
  return isqrt(d2 * METRES_PER_TILE * METRES_PER_TILE);
}

/**
 * Display form. Below one kilometre, rounded to the nearest 10 metres ("250 m").
 * From one kilometre, one decimal ("1.2 km", or "1,2 km" in Swedish and German). Integer
 * arithmetic only. A language the table does not name writes the English form.
 */
export function formatDistance(metres: number, locale: Locale): string {
  const tens = Math.floor((metres + 5) / 10) * 10;
  if (tens < 1000) return `${tens} m`;
  const tenths = Math.floor((metres + 50) / 100);
  const km = Math.floor(tenths / 10);
  const fraction = tenths % 10;
  const separator = DECIMAL[locale] ?? ".";
  return `${km}${separator}${fraction} km`;
}
